import type { InterpretiveReportDto } from '../../hooks/queries/useGetInterpretiveReportQuery';
import {
  scoreColourOf,
  toneOf,
  type TeamAgreement,
  type Tone,
} from '../../domain/psychological-safety/team-result';

/** The answers go from 1 to 5 */
const SCALE_MIN = 1;
const SCALE_MAX = 5;

/** The four dimensions the report names; a name the report does not know gets no icon or description */
export type DimensionKey = 'openness' | 'trust' | 'inclusion' | 'recognition';

const DIMENSION_KEYS: readonly string[] = ['openness', 'trust', 'inclusion', 'recognition'];

export type DimensionView = {
  key: DimensionKey | null;
  name: string;
  score: string;
  standardDeviation: string;
  /** How far along the bar the score reaches, from 0 to 100 */
  barPercent: number;
  status: Tone;
  agreement: TeamAgreement;
  /** Where the average and the band of one standard deviation lie on the 1–5 scale, from 0 to 100 */
  spread: { mean: number; bandStart: number; bandWidth: number };
  everydayImpact: string;
  reflectionQuestion: string;
};

const clampPercent = (value: number) => Math.min(100, Math.max(0, value));

const onScale = (value: number) =>
  clampPercent(((value - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * 100);

/** A score or a standard deviation with two decimals, as the report shows them */
export const decimal = (value: number, locale: string, digits = 2) =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);

/** The top of the scale, as «5,0» */
export const scaleMax = (locale: string) => decimal(SCALE_MAX, locale, 1);

/** The level the API sends, in the words of the domain; an unknown level reads as MODERATE */
export const agreementFromApi = (level: string): TeamAgreement =>
  level === 'HIGH' || level === 'LOW' ? level : 'MODERATE';

export const dimensionKeyOf = (name: string): DimensionKey | null =>
  DIMENSION_KEYS.includes(name) ? (name as DimensionKey) : null;

export const confidenceTone: Record<InterpretiveReportDto['methodology']['confidenceLevel'], Tone> = {
  high: 'success',
  medium: 'warning',
  low: 'danger',
};

/** The band covers one standard deviation on each side of the average, kept within the scale */
export const spreadOf = (score: number, standardDeviation: number) => {
  const bandStart = onScale(score - standardDeviation);
  const bandEnd = onScale(score + standardDeviation);
  return { mean: onScale(score), bandStart, bandWidth: bandEnd - bandStart };
};

export const dimensionView = (
  dimension: InterpretiveReportDto['dimensions'][number],
  locale: string,
): DimensionView => ({
  key: dimensionKeyOf(dimension.dimensionName),
  name: dimension.displayName,
  score: decimal(dimension.score, locale),
  standardDeviation: decimal(dimension.standardDeviation, locale),
  barPercent: clampPercent((dimension.score / SCALE_MAX) * 100),
  // The score alone, as on every other page; agreement has its own label
  status: toneOf[scoreColourOf(dimension.score)],
  agreement: agreementFromApi(dimension.agreementLevel),
  spread: spreadOf(dimension.score, dimension.standardDeviation),
  everydayImpact: dimension.everydayImpact,
  reflectionQuestion: dimension.reflectionQuestion,
});

/** Section numbers as the report prints them: «01», «02» … */
export const sectionNumber = (index: number) => String(index + 1).padStart(2, '0');

/**
 * The teams are in Norway, and the PDF is made on a server whose clock runs in UTC, so the date
 * follows Oslo time wherever the report is shown
 */
const REPORT_TIME_ZONE = 'Europe/Oslo';

export const reportDate = (createdAt: string, locale: string) => ({
  long: new Date(createdAt).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: REPORT_TIME_ZONE,
  }),
  short: new Date(createdAt).toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: REPORT_TIME_ZONE,
  }),
});

/** The response rate with one decimal and a percent sign, as «81,8 %» */
export const responseRate = (percent: number, locale: string) =>
  new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(percent / 100);

/** The locale the report is written in, so numbers and dates follow its language */
export const localeOf = (language: InterpretiveReportDto['language']) =>
  language === 'NB' ? 'nb-NO' : 'en-GB';
